import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  MagnifyingGlass, Globe, Lightning, UserPlus, Trash, CaretDown, CaretRight,
  FileText, Buildings, MapPin, Star, Warning, CheckCircle, XCircle, Spinner,
  Upload, ArrowRight, Target, Briefcase, ShieldCheck, DownloadSimple, Eye,
  Compass, FunnelSimple, X,
  Gavel, ArrowSquareOut, CurrencyDollar, Clock, Certificate,
  Bell, UsersThree, EnvelopeSimple, Phone, Funnel
} from '@phosphor-icons/react';

const TABS = [
  { key: 'analyze', label: 'Analizar', icon: MagnifyingGlass },
  { key: 'discover', label: 'Descubrimiento', icon: Compass },
  { key: 'bids', label: 'Licitaciones', icon: Gavel },
  { key: 'companies', label: 'Empresas', icon: UsersThree },
  { key: 'history', label: 'Historial', icon: FileText },
  { key: 'compliance', label: 'Requisitos', icon: ShieldCheck },
];

function ScoreBadge({ score }) {
  const s = Number(score) || 0;
  const color = s >= 75 ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
    : s >= 50 ? 'text-amber-700 bg-amber-50 border-amber-200'
    : 'text-red-700 bg-red-50 border-red-200';
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border ${color}`} data-testid="compatibility-score">
      <Star size={12} weight="fill" /> {s}%
    </span>
  );
}

function StatusBadge({ status }) {
  const map = {
    analyzed: { label: 'Analizado', cls: 'text-blue-700 bg-blue-50' },
    prospect_created: { label: 'Prospecto Creado', cls: 'text-emerald-700 bg-emerald-50' },
    error: { label: 'Error', cls: 'text-red-700 bg-red-50' },
  };
  const { label, cls } = map[status] || map.analyzed;
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{label}</span>;
}

export default function MarketIntelPage() {
  const [tab, setTab] = useState('analyze');
  const [url, setUrl] = useState('');
  const [bulkText, setBulkText] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState('');
  const [analyses, setAnalyses] = useState([]);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [similarLoading, setSimilarLoading] = useState(false);
  const [checklist, setChecklist] = useState([]);
  const [expandedCats, setExpandedCats] = useState({});
  const [inputMode, setInputMode] = useState('single'); // single | bulk
  // Discovery state
  const [discoveries, setDiscoveries] = useState([]);
  const [selectedDiscovery, setSelectedDiscovery] = useState(null);
  const [discoverLoading, setDiscoverLoading] = useState(false);
  const [analyzingUrl, setAnalyzingUrl] = useState(null);
  const [discoverForm, setDiscoverForm] = useState({
    states: ['Florida'],
    industries: ['General Contractor', 'Construction Management'],
    project_types: [],
    min_size: 'any',
    keywords: '',
  });
  const [stateInput, setStateInput] = useState('');
  const [directories, setDirectories] = useState([]);
  const [usStates, setUsStates] = useState([]);
  // Bids state
  const [bidSources, setBidSources] = useState([]);
  const [bidSearches, setBidSearches] = useState([]);
  const [selectedBidSearch, setSelectedBidSearch] = useState(null);
  const [bidLoading, setBidLoading] = useState(false);
  const [bidForm, setBidForm] = useState({
    states: ['Florida'],
    keywords: 'low voltage cabling CCTV access control',
    bid_types: [],
    budget_range: 'any',
  });
  const [bidStateInput, setBidStateInput] = useState('');
  // Alert config state
  const [alertConfig, setAlertConfig] = useState(null);
  const [alertSaving, setAlertSaving] = useState(false);
  const [alertRunning, setAlertRunning] = useState(false);
  const [alertHistory, setAlertHistory] = useState([]);
  const [newRecipient, setNewRecipient] = useState('');
  // Company search state
  const [companySearches, setCompanySearches] = useState([]);
  const [selectedCompanySearch, setSelectedCompanySearch] = useState(null);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [companyForm, setCompanyForm] = useState({
    sectors: [],
    locations: ['Florida'],
    min_employees: 20,
    keywords: '',
  });
  const [companySectorInput, setCompanySectorInput] = useState('');
  const [companyLocationInput, setCompanyLocationInput] = useState('');
  // Bid status filter
  const [bidStatusFilter, setBidStatusFilter] = useState('all');

  const fetchAnalyses = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/b2b/market-intel/analyses`, { withCredentials: true });
      setAnalyses(res.data || []);
    } catch {}
  }, []);

  const fetchChecklist = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/b2b/market-intel/compliance-checklist`, { withCredentials: true });
      setChecklist(res.data.checklist || []);
    } catch {}
  }, []);

  const fetchDiscoveries = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/b2b/market-intel/discoveries`, { withCredentials: true });
      setDiscoveries(res.data || []);
    } catch {}
  }, []);

  const fetchDirectories = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/b2b/market-intel/directories`, { withCredentials: true });
      setDirectories(res.data.directories || []);
      setUsStates(res.data.us_states || []);
    } catch {}
  }, []);

  const fetchBidSources = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/b2b/market-intel/bid-sources`, { withCredentials: true });
      setBidSources(res.data.sources || []);
    } catch {}
  }, []);

  const fetchBidSearches = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/b2b/market-intel/bid-searches`, { withCredentials: true });
      setBidSearches(res.data || []);
    } catch {}
  }, []);

  const fetchAlertConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/b2b/market-intel/bid-alerts/config`, { withCredentials: true });
      setAlertConfig(res.data || null);
    } catch {}
  }, []);

  const fetchAlertHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/b2b/market-intel/bid-alerts/history`, { withCredentials: true });
      setAlertHistory(res.data || []);
    } catch {}
  }, []);

  const fetchCompanySearches = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/b2b/market-intel/company-searches`, { withCredentials: true });
      setCompanySearches(res.data || []);
    } catch {}
  }, []);

  useEffect(() => {
    fetchAnalyses();
    fetchChecklist();
    fetchDiscoveries();
    fetchDirectories();
    fetchBidSources();
    fetchBidSearches();
    fetchAlertConfig();
    fetchAlertHistory();
    fetchCompanySearches();
  }, [fetchAnalyses, fetchChecklist, fetchDiscoveries, fetchDirectories, fetchBidSources, fetchBidSearches, fetchAlertConfig, fetchAlertHistory, fetchCompanySearches]);

  const handleAnalyzeUrl = async () => {
    if (!url.trim()) return;
    setLoading(true);
    setLoadingMsg('Analizando sitio web y buscando empresas similares...');
    try {
      const res = await axios.post(`${API_URL}/api/b2b/market-intel/analyze-url`, { url: url.trim() }, { withCredentials: true });
      if (res.data.already_exists) {
        alert('Esta URL ya fue analizada. Mostrando resultados existentes.');
      }
      setSelectedAnalysis(res.data);
      setUrl('');
      fetchAnalyses();
      setTab('history');
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al analizar la URL');
    } finally {
      setLoading(false);
      setLoadingMsg('');
    }
  };

  const handleAnalyzeBulk = async () => {
    const urls = bulkText.split('\n').map(u => u.trim()).filter(u => u.length > 0);
    if (urls.length === 0) return;
    setLoading(true);
    setLoadingMsg(`Analizando ${urls.length} sitios web...`);
    try {
      await axios.post(`${API_URL}/api/b2b/market-intel/analyze-bulk`, { urls }, { withCredentials: true });
      setBulkText('');
      fetchAnalyses();
      setTab('history');
    } catch (err) {
      alert(err.response?.data?.detail || 'Error en analisis masivo');
    } finally {
      setLoading(false);
      setLoadingMsg('');
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setBulkText(ev.target.result);
    };
    reader.readAsText(file);
  };

  const handleFindSimilar = async (analysisId) => {
    setSimilarLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/b2b/market-intel/find-similar/${analysisId}`, {}, { withCredentials: true });
      setSelectedAnalysis(prev => ({ ...prev, similar_companies: res.data }));
      fetchAnalyses();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error buscando empresas similares');
    } finally {
      setSimilarLoading(false);
    }
  };

  const handleCreateProspect = async (analysisId) => {
    try {
      await axios.post(`${API_URL}/api/b2b/market-intel/create-prospect/${analysisId}`, {}, { withCredentials: true });
      alert('Prospecto creado exitosamente');
      fetchAnalyses();
      if (selectedAnalysis?.id === analysisId) {
        setSelectedAnalysis(prev => ({ ...prev, status: 'prospect_created' }));
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Error creando prospecto');
    }
  };

  const handleCreateProspectFromSimilar = async (company) => {
    try {
      await axios.post(`${API_URL}/api/b2b/market-intel/create-prospect-from-similar`, { company }, { withCredentials: true });
      alert(`Prospecto creado: ${company.company_name}`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error creando prospecto');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar este analisis?')) return;
    try {
      await axios.delete(`${API_URL}/api/b2b/market-intel/analyses/${id}`, { withCredentials: true });
      if (selectedAnalysis?.id === id) setSelectedAnalysis(null);
      fetchAnalyses();
    } catch {}
  };

  const handleDiscover = async () => {
    setDiscoverLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/b2b/market-intel/discover`, discoverForm, { withCredentials: true });
      setSelectedDiscovery(res.data);
      fetchDiscoveries();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error en descubrimiento');
    } finally {
      setDiscoverLoading(false);
    }
  };

  const handleAnalyzeDiscovered = async (company) => {
    if (!company.website) {
      alert('Esta empresa no tiene URL de sitio web disponible');
      return;
    }
    setAnalyzingUrl(company.website);
    try {
      const res = await axios.post(`${API_URL}/api/b2b/market-intel/discover-and-analyze`,
        { url: company.website, company_name: company.company_name },
        { withCredentials: true }
      );
      if (res.data.already_exists) {
        setSelectedAnalysis(res.data.analysis);
        setTab('history');
      } else {
        setSelectedAnalysis(res.data.analysis);
        fetchAnalyses();
        setTab('history');
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al analizar empresa');
    } finally {
      setAnalyzingUrl(null);
    }
  };

  const handleDeleteDiscovery = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/b2b/market-intel/discoveries/${id}`, { withCredentials: true });
      if (selectedDiscovery?.id === id) setSelectedDiscovery(null);
      fetchDiscoveries();
    } catch {}
  };

  const addState = (state) => {
    if (state && !discoverForm.states.includes(state)) {
      setDiscoverForm(p => ({ ...p, states: [...p.states, state] }));
    }
    setStateInput('');
  };

  const removeState = (state) => {
    setDiscoverForm(p => ({ ...p, states: p.states.filter(s => s !== state) }));
  };

  // Bid handlers
  const handleSearchBids = async () => {
    setBidLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/b2b/market-intel/search-bids`, bidForm, { withCredentials: true });
      setSelectedBidSearch(res.data);
      fetchBidSearches();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error buscando licitaciones');
    } finally {
      setBidLoading(false);
    }
  };

  const handleCreateProspectFromBid = async (opportunity) => {
    try {
      await axios.post(`${API_URL}/api/b2b/market-intel/create-prospect-from-bid`, { opportunity }, { withCredentials: true });
      alert(`Prospecto creado: ${opportunity.agency_or_company}`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error creando prospecto');
    }
  };

  const handleDeleteBidSearch = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/b2b/market-intel/bid-searches/${id}`, { withCredentials: true });
      if (selectedBidSearch?.id === id) setSelectedBidSearch(null);
      fetchBidSearches();
    } catch {}
  };

  const addBidState = (state) => {
    if (state && !bidForm.states.includes(state)) {
      setBidForm(p => ({ ...p, states: [...p.states, state] }));
    }
    setBidStateInput('');
  };

  const removeBidState = (state) => {
    setBidForm(p => ({ ...p, states: p.states.filter(s => s !== state) }));
  };

  const handleSaveAlertConfig = async (updates) => {
    setAlertSaving(true);
    try {
      const payload = { ...alertConfig, ...updates };
      const res = await axios.post(`${API_URL}/api/b2b/market-intel/bid-alerts/config`, payload, { withCredentials: true });
      setAlertConfig(res.data.config);
      alert('Configuracion de alertas guardada');
    } catch (err) {
      alert(err.response?.data?.detail || 'Error guardando configuracion');
    } finally {
      setAlertSaving(false);
    }
  };

  const handleRunAlertNow = async () => {
    setAlertRunning(true);
    try {
      const res = await axios.post(`${API_URL}/api/b2b/market-intel/bid-alerts/run-now`, {}, { withCredentials: true });
      alert(res.data.message);
      fetchAlertConfig();
      fetchAlertHistory();
      fetchBidSearches();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error ejecutando alerta');
    } finally {
      setAlertRunning(false);
    }
  };

  const addAlertRecipient = () => {
    if (!newRecipient.trim() || !newRecipient.includes('@')) return;
    const updated = [...(alertConfig?.recipients || []), newRecipient.trim()];
    setAlertConfig(p => ({ ...p, recipients: updated }));
    setNewRecipient('');
  };

  const removeAlertRecipient = (email) => {
    const updated = (alertConfig?.recipients || []).filter(r => r !== email);
    setAlertConfig(p => ({ ...p, recipients: updated }));
  };

  // Company Search handlers
  const PRESET_SECTORS = [
    'Empresas de Inversiones', 'Hoteles', 'Centros Educativos', 'Universidades',
    'Almacenes de Cadena', 'Cadenas de Comida', 'Restaurantes', 'Hospitales',
    'Centros Comerciales', 'Oficinas Corporativas', 'Data Centers', 'Bancos'
  ];

  const addCompanySector = (sector) => {
    if (sector && !companyForm.sectors.includes(sector)) {
      setCompanyForm(p => ({ ...p, sectors: [...p.sectors, sector] }));
    }
    setCompanySectorInput('');
  };

  const removeCompanySector = (sector) => {
    setCompanyForm(p => ({ ...p, sectors: p.sectors.filter(s => s !== sector) }));
  };

  const addCompanyLocation = (loc) => {
    if (loc && !companyForm.locations.includes(loc)) {
      setCompanyForm(p => ({ ...p, locations: [...p.locations, loc] }));
    }
    setCompanyLocationInput('');
  };

  const removeCompanyLocation = (loc) => {
    setCompanyForm(p => ({ ...p, locations: p.locations.filter(l => l !== loc) }));
  };

  const handleSearchCompanies = async () => {
    if (companyForm.sectors.length === 0) {
      alert('Selecciona al menos un sector');
      return;
    }
    setCompanyLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/b2b/market-intel/search-companies`, companyForm, { withCredentials: true });
      setSelectedCompanySearch(res.data);
      fetchCompanySearches();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error buscando empresas');
    } finally {
      setCompanyLoading(false);
    }
  };

  const handleCreateProspectFromCompany = async (company) => {
    try {
      await axios.post(`${API_URL}/api/b2b/market-intel/create-prospect-from-company`, { company }, { withCredentials: true });
      alert(`Prospecto creado: ${company.name}`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error creando prospecto');
    }
  };

  const handleDeleteCompanySearch = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/b2b/market-intel/company-searches/${id}`, { withCredentials: true });
      if (selectedCompanySearch?.id === id) setSelectedCompanySearch(null);
      fetchCompanySearches();
    } catch {}
  };

  const toggleCat = (cat) => setExpandedCats(p => ({ ...p, [cat]: !p[cat] }));

  return (
    <div className="space-y-6" data-testid="market-intel-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Inteligencia de Mercado</h1>
          <p className="text-sm text-slate-500 mt-1">Analiza empresas GC, encuentra subcontrataciones y genera prospectos automaticamente</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-slate-100 rounded-lg w-fit" data-testid="market-intel-tabs">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                tab === t.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid={`tab-${t.key}`}
            >
              <Icon size={16} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Loading overlay */}
      {loading && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 flex items-center gap-4" data-testid="loading-overlay">
          <Spinner size={28} className="text-blue-600 animate-spin" />
          <div>
            <p className="font-medium text-blue-900">{loadingMsg}</p>
            <p className="text-sm text-blue-600 mt-0.5">Esto puede tomar 15-30 segundos por URL...</p>
          </div>
        </div>
      )}

      {/* ==================== ANALYZE TAB ==================== */}
      {tab === 'analyze' && !loading && (
        <div className="space-y-6">
          {/* Input mode toggle */}
          <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="analyze-section">
            <div className="flex gap-3 mb-5">
              <button
                onClick={() => setInputMode('single')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-all ${
                  inputMode === 'single' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'
                }`}
                data-testid="mode-single"
              >
                <Globe size={16} /> URL Individual
              </button>
              <button
                onClick={() => setInputMode('bulk')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-all ${
                  inputMode === 'bulk' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'
                }`}
                data-testid="mode-bulk"
              >
                <Upload size={16} /> Carga Masiva
              </button>
            </div>

            {inputMode === 'single' ? (
              <div className="flex gap-3">
                <div className="flex-1 relative">
                  <Globe size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAnalyzeUrl()}
                    placeholder="https://www.ejemplo-gc.com"
                    className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    data-testid="url-input"
                  />
                </div>
                <button
                  onClick={handleAnalyzeUrl}
                  disabled={!url.trim()}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
                  data-testid="analyze-btn"
                >
                  <Lightning size={18} weight="fill" /> Analizar
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 px-4 py-2 border border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors text-sm text-slate-600">
                    <Upload size={16} />
                    <span>Cargar archivo (.txt, .csv)</span>
                    <input type="file" accept=".txt,.csv" className="hidden" onChange={handleFileUpload} data-testid="file-upload" />
                  </label>
                  <span className="text-xs text-slate-400">o pega las URLs abajo (una por linea)</span>
                </div>
                <textarea
                  value={bulkText}
                  onChange={(e) => setBulkText(e.target.value)}
                  placeholder={"https://www.jrmcm.com\nhttps://www.unitelgroup.com\nhttps://otro-gc.com"}
                  rows={6}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 font-mono"
                  data-testid="bulk-textarea"
                />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400">{bulkText.split('\n').filter(l => l.trim()).length} URLs detectadas (max 20)</span>
                  <button
                    onClick={handleAnalyzeBulk}
                    disabled={!bulkText.trim()}
                    className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
                    data-testid="bulk-analyze-btn"
                  >
                    <Lightning size={16} weight="fill" /> Analizar Todas
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Quick tips */}
          <div className="bg-slate-800 rounded-xl p-5 text-white">
            <h3 className="font-semibold text-sm mb-3 flex items-center gap-2"><Target size={16} /> Consejos de Uso</h3>
            <div className="grid sm:grid-cols-3 gap-4 text-xs text-slate-300">
              <div>
                <p className="font-medium text-slate-100 mb-1">Paginas ideales para analizar</p>
                <p>Sitios de GC con paginas "About", "Services", "Subcontractors", "Trade Partners" o "Vendor Registration".</p>
              </div>
              <div>
                <p className="font-medium text-slate-100 mb-1">Empresas similares</p>
                <p>Despues del analisis, usa "Buscar Similares" para que la IA encuentre mas GCs compatibles.</p>
              </div>
              <div>
                <p className="font-medium text-slate-100 mb-1">Flujo completo</p>
                <p>Analizar URL → Revisar compatibilidad → Crear Prospecto → Enviar campana de email B2B.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ==================== DISCOVER TAB ==================== */}
      {tab === 'discover' && (
        <div className="space-y-6">
          {/* Search form */}
          <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="discover-section">
            <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Compass size={16} className="text-purple-600" /> Descubrir General Contractors
            </h3>
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              {/* States */}
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">Estados (donde operan o ejecutan proyectos)</label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {discoverForm.states.map(s => (
                    <span key={s} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs border border-blue-200">
                      {s}
                      <button onClick={() => removeState(s)} className="hover:text-red-500"><X size={10} /></button>
                    </span>
                  ))}
                </div>
                <div className="relative">
                  <input
                    type="text"
                    value={stateInput}
                    onChange={(e) => setStateInput(e.target.value)}
                    placeholder="Agregar estado..."
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    list="us-states-list"
                    data-testid="state-input"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); addState(stateInput); }
                    }}
                  />
                  <datalist id="us-states-list">
                    {usStates.filter(s => !discoverForm.states.includes(s)).map(s => (
                      <option key={s} value={s} />
                    ))}
                  </datalist>
                </div>
                <div className="flex gap-1 mt-1.5">
                  {['Florida', 'Texas', 'New York', 'California'].map(s => (
                    !discoverForm.states.includes(s) && (
                      <button key={s} onClick={() => addState(s)} className="text-xs px-2 py-0.5 text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors">
                        + {s}
                      </button>
                    )
                  ))}
                </div>
              </div>

              {/* Industries */}
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">Tipo de Empresa</label>
                <div className="space-y-1.5">
                  {['General Contractor', 'Construction Management', 'Electrical Contractor', 'Property Management', 'Real Estate Developer'].map(ind => (
                    <label key={ind} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={discoverForm.industries.includes(ind)}
                        onChange={(e) => {
                          setDiscoverForm(p => ({
                            ...p,
                            industries: e.target.checked
                              ? [...p.industries, ind]
                              : p.industries.filter(i => i !== ind)
                          }));
                        }}
                        className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-slate-700">{ind}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              {/* Project types */}
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">Tipos de Proyecto (opcional)</label>
                <div className="flex flex-wrap gap-1.5">
                  {['Comercial', 'Hospitality', 'Salud', 'Educacion', 'Retail', 'Industrial', 'Residencial', 'Gobierno'].map(pt => (
                    <button
                      key={pt}
                      onClick={() => {
                        setDiscoverForm(p => ({
                          ...p,
                          project_types: p.project_types.includes(pt)
                            ? p.project_types.filter(t => t !== pt)
                            : [...p.project_types, pt]
                        }));
                      }}
                      className={`px-2.5 py-1 rounded-full text-xs border transition-all ${
                        discoverForm.project_types.includes(pt)
                          ? 'bg-purple-50 text-purple-700 border-purple-200'
                          : 'bg-slate-50 text-slate-500 border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      {pt}
                    </button>
                  ))}
                </div>
              </div>

              {/* Keywords */}
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">Palabras Clave Adicionales</label>
                <input
                  type="text"
                  value={discoverForm.keywords}
                  onChange={(e) => setDiscoverForm(p => ({ ...p, keywords: e.target.value }))}
                  placeholder="ej: low voltage subcontractor, cabling, security"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  data-testid="keywords-input"
                />
              </div>
            </div>

            <button
              onClick={handleDiscover}
              disabled={discoverLoading || discoverForm.states.length === 0}
              className="px-6 py-2.5 bg-purple-600 text-white rounded-lg font-medium text-sm hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
              data-testid="discover-btn"
            >
              {discoverLoading ? <Spinner size={16} className="animate-spin" /> : <Compass size={16} />}
              {discoverLoading ? 'Buscando empresas...' : 'Descubrir Empresas'}
            </button>
          </div>

          {/* Loading indicator */}
          {discoverLoading && (
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-6 flex items-center gap-4">
              <Spinner size={28} className="text-purple-600 animate-spin" />
              <div>
                <p className="font-medium text-purple-900">Buscando General Contractors con IA...</p>
                <p className="text-sm text-purple-600 mt-0.5">Analizando directorios y bases de datos. Esto puede tomar 20-40 segundos...</p>
              </div>
            </div>
          )}

          {/* Discovery Results */}
          {selectedDiscovery && !discoverLoading && (
            <DiscoveryResults
              discovery={selectedDiscovery}
              onAnalyze={handleAnalyzeDiscovered}
              onCreateProspect={handleCreateProspectFromSimilar}
              analyzingUrl={analyzingUrl}
            />
          )}

          {/* Previous Discoveries */}
          {discoveries.length > 0 && !discoverLoading && (
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="discovery-history">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Busquedas Anteriores</h4>
              <div className="space-y-2">
                {discoveries.map(d => (
                  <div key={d.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <button onClick={() => setSelectedDiscovery(d)} className="flex-1 text-left">
                      <p className="text-sm font-medium text-slate-800">
                        {d.criteria?.states?.join(', ')} - {d.criteria?.industries?.join(', ')}
                      </p>
                      <p className="text-xs text-slate-400">
                        {d.companies_found} empresas encontradas - {new Date(d.created_at).toLocaleDateString('es')}
                      </p>
                    </button>
                    <button onClick={() => handleDeleteDiscovery(d.id)} className="p-1.5 text-slate-400 hover:text-red-500">
                      <Trash size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Known directories info */}
          {directories.length > 0 && (
            <div className="bg-slate-800 rounded-xl p-5 text-white">
              <h3 className="font-semibold text-sm mb-3 flex items-center gap-2"><Globe size={16} /> Directorios de Referencia</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {directories.map((d, i) => (
                  <a key={i} href={d.url} target="_blank" rel="noopener noreferrer"
                    className="block p-3 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors">
                    <p className="text-sm font-medium text-slate-100">{d.name}</p>
                    <p className="text-xs text-slate-400 mt-0.5 capitalize">{d.type}</p>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ==================== BIDS TAB ==================== */}
      {tab === 'bids' && (
        <div className="space-y-6">
          {/* Search form */}
          <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="bid-search-section">
            <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Gavel size={16} className="text-amber-600" /> Buscar Licitaciones y Oportunidades
            </h3>
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">Estados</label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {bidForm.states.map(s => (
                    <span key={s} className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full text-xs border border-amber-200">
                      {s}
                      <button onClick={() => removeBidState(s)} className="hover:text-red-500"><X size={10} /></button>
                    </span>
                  ))}
                </div>
                <input
                  type="text"
                  value={bidStateInput}
                  onChange={(e) => setBidStateInput(e.target.value)}
                  placeholder="Agregar estado..."
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                  list="bid-states-list"
                  data-testid="bid-state-input"
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addBidState(bidStateInput); } }}
                />
                <datalist id="bid-states-list">
                  {usStates.filter(s => !bidForm.states.includes(s)).map(s => (
                    <option key={s} value={s} />
                  ))}
                </datalist>
                <div className="flex gap-1 mt-1.5">
                  {['Florida', 'Texas', 'New York', 'California'].map(s => (
                    !bidForm.states.includes(s) && (
                      <button key={s} onClick={() => addBidState(s)} className="text-xs px-2 py-0.5 text-amber-600 bg-amber-50 rounded hover:bg-amber-100 transition-colors">+ {s}</button>
                    )
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">Palabras Clave</label>
                <input
                  type="text"
                  value={bidForm.keywords}
                  onChange={(e) => setBidForm(p => ({ ...p, keywords: e.target.value }))}
                  placeholder="low voltage, cabling, CCTV, access control..."
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                  data-testid="bid-keywords-input"
                />
                <label className="text-xs font-medium text-slate-500 mt-3 mb-1 block">Tipo de Licitacion</label>
                <div className="flex flex-wrap gap-1.5">
                  {['ITB', 'RFP', 'RFQ', 'Subcontract'].map(bt => (
                    <button
                      key={bt}
                      onClick={() => setBidForm(p => ({
                        ...p,
                        bid_types: p.bid_types.includes(bt) ? p.bid_types.filter(t => t !== bt) : [...p.bid_types, bt]
                      }))}
                      className={`px-2.5 py-1 rounded-full text-xs border transition-all ${
                        bidForm.bid_types.includes(bt)
                          ? 'bg-amber-50 text-amber-700 border-amber-200'
                          : 'bg-slate-50 text-slate-500 border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      {bt}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <button
              onClick={handleSearchBids}
              disabled={bidLoading || bidForm.states.length === 0}
              className="px-6 py-2.5 bg-amber-600 text-white rounded-lg font-medium text-sm hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
              data-testid="search-bids-btn"
            >
              {bidLoading ? <Spinner size={16} className="animate-spin" /> : <MagnifyingGlass size={16} />}
              {bidLoading ? 'Buscando licitaciones...' : 'Buscar Licitaciones'}
            </button>
          </div>

          {bidLoading && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 flex items-center gap-4">
              <Spinner size={28} className="text-amber-600 animate-spin" />
              <div>
                <p className="font-medium text-amber-900">Buscando oportunidades de licitacion...</p>
                <p className="text-sm text-amber-600 mt-0.5">Analizando plataformas federales, estatales y locales. 20-40 segundos...</p>
              </div>
            </div>
          )}

          {/* Bid Results */}
          {selectedBidSearch && !bidLoading && (
            <BidResults
              search={selectedBidSearch}
              onCreateProspect={handleCreateProspectFromBid}
            />
          )}

          {/* Previous Searches */}
          {bidSearches.length > 0 && !bidLoading && (
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="bid-search-history">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Busquedas Anteriores</h4>
              <div className="space-y-2">
                {bidSearches.map(bs => (
                  <div key={bs.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <button onClick={() => setSelectedBidSearch(bs)} className="flex-1 text-left">
                      <p className="text-sm font-medium text-slate-800">
                        {bs.criteria?.states?.join(', ')} - "{bs.criteria?.keywords}"
                      </p>
                      <p className="text-xs text-slate-400">
                        {bs.opportunities_found} oportunidades - {new Date(bs.created_at).toLocaleDateString('es')}
                      </p>
                    </button>
                    <button onClick={() => handleDeleteBidSearch(bs.id)} className="p-1.5 text-slate-400 hover:text-red-500">
                      <Trash size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bid Sources Directory */}
          {bidSources.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="bid-sources">
              <h4 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                <Globe size={16} /> Plataformas de Licitaciones
              </h4>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {bidSources.map((src, i) => {
                  const typeColor = {
                    'Federal': 'bg-blue-50 text-blue-700 border-blue-200',
                    'Estado': 'bg-purple-50 text-purple-700 border-purple-200',
                    'Local': 'bg-emerald-50 text-emerald-700 border-emerald-200',
                    'Agregador': 'bg-amber-50 text-amber-700 border-amber-200',
                    'Comercial': 'bg-pink-50 text-pink-700 border-pink-200',
                  }[src.type] || 'bg-slate-50 text-slate-700 border-slate-200';
                  return (
                    <div key={i} className="border border-slate-200 rounded-lg p-3 hover:shadow-sm transition-shadow" data-testid={`bid-source-${i}`}>
                      <div className="flex items-start justify-between mb-1.5">
                        <h5 className="text-sm font-semibold text-slate-900 leading-tight">{src.name}</h5>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${typeColor}`}>{src.type}</span>
                      </div>
                      <p className="text-xs text-slate-500 mb-2 line-clamp-2">{src.description}</p>
                      <div className="flex gap-2">
                        {src.search_url && (
                          <a href={src.search_url} target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded text-[10px] font-medium hover:bg-blue-700 transition-colors">
                            <MagnifyingGlass size={10} /> Buscar
                          </a>
                        )}
                        <a href={src.url} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-1 px-2 py-1 border border-slate-200 text-slate-600 rounded text-[10px] font-medium hover:bg-slate-50 transition-colors">
                          <ArrowSquareOut size={10} /> Sitio
                        </a>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Alert Configuration */}
          {alertConfig && (
            <div className="bg-white rounded-xl border-2 border-amber-200 p-5" data-testid="alert-config">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                  <Bell size={16} className="text-amber-600" /> Alertas Automaticas por Email
                </h4>
                <label className="flex items-center gap-2 cursor-pointer">
                  <span className="text-xs text-slate-500">{alertConfig.enabled ? 'Activado' : 'Desactivado'}</span>
                  <button
                    onClick={() => handleSaveAlertConfig({ enabled: !alertConfig.enabled })}
                    className={`relative w-10 h-5 rounded-full transition-colors ${alertConfig.enabled ? 'bg-emerald-500' : 'bg-slate-300'}`}
                    data-testid="alert-toggle"
                  >
                    <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${alertConfig.enabled ? 'left-5' : 'left-0.5'}`} />
                  </button>
                </label>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Frecuencia</label>
                  <select
                    value={alertConfig.frequency || 'daily'}
                    onChange={(e) => setAlertConfig(p => ({ ...p, frequency: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                    data-testid="alert-frequency"
                  >
                    <option value="daily">Diario</option>
                    <option value="weekly">Semanal</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Score Minimo de Compatibilidad</label>
                  <select
                    value={alertConfig.min_compatibility_score || 50}
                    onChange={(e) => setAlertConfig(p => ({ ...p, min_compatibility_score: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                    data-testid="alert-min-score"
                  >
                    <option value={0}>Todos (0%+)</option>
                    <option value={30}>30%+</option>
                    <option value={50}>50%+</option>
                    <option value={70}>70%+</option>
                    <option value={80}>80%+</option>
                  </select>
                </div>
              </div>

              <div className="mb-4">
                <label className="text-xs font-medium text-slate-500 mb-1.5 block">Destinatarios</label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {(alertConfig.recipients || []).map(r => (
                    <span key={r} className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs border border-blue-200">
                      {r}
                      <button onClick={() => removeAlertRecipient(r)} className="hover:text-red-500"><X size={10} /></button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={newRecipient}
                    onChange={(e) => setNewRecipient(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAlertRecipient(); } }}
                    placeholder="email@empresa.com"
                    className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                    data-testid="alert-recipient-input"
                  />
                  <button
                    onClick={addAlertRecipient}
                    className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                    data-testid="alert-add-recipient"
                  >
                    Agregar
                  </button>
                </div>
              </div>

              <div className="bg-slate-50 rounded-lg p-3 mb-4 text-xs text-slate-500">
                <p className="font-medium text-slate-700 mb-1">Criterios de busqueda:</p>
                <p>Estados: {alertConfig.search_criteria?.states?.join(', ') || 'Florida'}</p>
                <p>Keywords: {alertConfig.search_criteria?.keywords || 'low voltage'}</p>
                {alertConfig.last_run && (
                  <p className="mt-1 text-slate-400">
                    Ultima ejecucion: {new Date(alertConfig.last_run).toLocaleString('es')} ({alertConfig.last_results_count || 0} resultados)
                  </p>
                )}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => handleSaveAlertConfig(alertConfig)}
                  disabled={alertSaving}
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 disabled:opacity-50 flex items-center gap-1.5 transition-colors"
                  data-testid="alert-save-btn"
                >
                  {alertSaving ? <Spinner size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                  Guardar Configuracion
                </button>
                <button
                  onClick={handleRunAlertNow}
                  disabled={alertRunning || !(alertConfig.recipients?.length > 0)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5 transition-colors"
                  data-testid="alert-run-now-btn"
                >
                  {alertRunning ? <Spinner size={14} className="animate-spin" /> : <Lightning size={14} weight="fill" />}
                  {alertRunning ? 'Ejecutando...' : 'Ejecutar Ahora'}
                </button>
              </div>
            </div>
          )}

          {/* Alert History */}
          {alertHistory.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="alert-history">
              <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <Clock size={14} /> Historial de Alertas Automaticas
              </h4>
              <div className="space-y-2">
                {alertHistory.slice(0, 10).map(ah => (
                  <div key={ah.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg text-sm">
                    <div>
                      <p className="font-medium text-slate-800">{ah.filtered_count || ah.opportunities_found} oportunidades</p>
                      <p className="text-xs text-slate-400">{new Date(ah.created_at).toLocaleString('es')}</p>
                    </div>
                    <button onClick={() => setSelectedBidSearch(ah)} className="text-xs text-blue-600 hover:underline">Ver resultados</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ==================== COMPANIES TAB ==================== */}
      {tab === 'companies' && (
        <div className="space-y-6">
          {/* Search Form */}
          <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="company-search-section">
            <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <UsersThree size={16} className="text-indigo-600" /> Buscar Empresas por Sector (+{companyForm.min_employees} empleados)
            </h3>
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">Sectores</label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {companyForm.sectors.map(s => (
                    <span key={s} className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full text-xs border border-indigo-200">
                      {s}
                      <button onClick={() => removeCompanySector(s)} className="hover:text-red-500"><X size={10} /></button>
                    </span>
                  ))}
                </div>
                <input
                  type="text"
                  value={companySectorInput}
                  onChange={(e) => setCompanySectorInput(e.target.value)}
                  placeholder="Agregar sector..."
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                  data-testid="company-sector-input"
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCompanySector(companySectorInput); } }}
                />
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {PRESET_SECTORS.filter(s => !companyForm.sectors.includes(s)).slice(0, 6).map(s => (
                    <button key={s} onClick={() => addCompanySector(s)} className="text-xs px-2 py-0.5 text-indigo-600 bg-indigo-50 rounded hover:bg-indigo-100 transition-colors">+ {s}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">Ubicaciones</label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {companyForm.locations.map(l => (
                    <span key={l} className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full text-xs border border-emerald-200">
                      {l}
                      <button onClick={() => removeCompanyLocation(l)} className="hover:text-red-500"><X size={10} /></button>
                    </span>
                  ))}
                </div>
                <input
                  type="text"
                  value={companyLocationInput}
                  onChange={(e) => setCompanyLocationInput(e.target.value)}
                  placeholder="Agregar ubicacion..."
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                  data-testid="company-location-input"
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCompanyLocation(companyLocationInput); } }}
                  list="company-locations-list"
                />
                <datalist id="company-locations-list">
                  {usStates.filter(s => !companyForm.locations.includes(s)).map(s => (
                    <option key={s} value={s} />
                  ))}
                </datalist>
                <div className="flex gap-1 mt-1.5">
                  {['Florida', 'Texas', 'New York', 'California'].map(s => (
                    !companyForm.locations.includes(s) && (
                      <button key={s} onClick={() => addCompanyLocation(s)} className="text-xs px-2 py-0.5 text-emerald-600 bg-emerald-50 rounded hover:bg-emerald-100 transition-colors">+ {s}</button>
                    )
                  ))}
                </div>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">Minimo de Empleados</label>
                <select
                  value={companyForm.min_employees}
                  onChange={(e) => setCompanyForm(p => ({ ...p, min_employees: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                  data-testid="company-min-employees"
                >
                  <option value={10}>10+ empleados</option>
                  <option value={20}>20+ empleados</option>
                  <option value={50}>50+ empleados</option>
                  <option value={100}>100+ empleados</option>
                  <option value={250}>250+ empleados</option>
                  <option value={500}>500+ empleados</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">Palabras Clave (opcional)</label>
                <input
                  type="text"
                  value={companyForm.keywords}
                  onChange={(e) => setCompanyForm(p => ({ ...p, keywords: e.target.value }))}
                  placeholder="tecnologia, expansion, nuevo edificio..."
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                  data-testid="company-keywords-input"
                />
              </div>
            </div>
            <button
              onClick={handleSearchCompanies}
              disabled={companyLoading || companyForm.sectors.length === 0}
              className="px-6 py-2.5 bg-indigo-600 text-white rounded-lg font-medium text-sm hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
              data-testid="search-companies-btn"
            >
              {companyLoading ? <Spinner size={16} className="animate-spin" /> : <MagnifyingGlass size={16} />}
              {companyLoading ? 'Buscando empresas...' : 'Buscar Empresas'}
            </button>
          </div>

          {companyLoading && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-6 flex items-center gap-4">
              <Spinner size={28} className="text-indigo-600 animate-spin" />
              <div>
                <p className="font-medium text-indigo-900">Buscando empresas en los sectores seleccionados...</p>
                <p className="text-sm text-indigo-600 mt-0.5">Analizando empresas con IA. 20-40 segundos...</p>
              </div>
            </div>
          )}

          {/* Company Results */}
          {selectedCompanySearch && !companyLoading && (
            <CompanyResults
              search={selectedCompanySearch}
              onCreateProspect={handleCreateProspectFromCompany}
            />
          )}

          {/* Previous Searches */}
          {companySearches.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="company-previous-searches">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Busquedas Anteriores</h4>
              <div className="space-y-2">
                {companySearches.map(cs => (
                  <div key={cs.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer" data-testid={`company-search-${cs.id}`}>
                    <div onClick={() => setSelectedCompanySearch(cs)} className="flex-1">
                      <p className="text-sm font-medium text-slate-800">{cs.criteria?.sectors?.join(', ') || 'Todos'} — {cs.companies_found} empresas</p>
                      <p className="text-xs text-slate-400">{cs.criteria?.locations?.join(', ')} | {new Date(cs.created_at).toLocaleString('es')}</p>
                    </div>
                    <button onClick={() => handleDeleteCompanySearch(cs.id)} className="p-1.5 text-slate-400 hover:text-red-500 transition-colors">
                      <Trash size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ==================== HISTORY TAB ==================== */}
      {tab === 'history' && (
        <div className="grid lg:grid-cols-5 gap-6">
          {/* List */}
          <div className="lg:col-span-2 space-y-2" data-testid="analysis-list">
            <h3 className="text-sm font-semibold text-slate-700 mb-2">{analyses.length} analisis realizados</h3>
            {analyses.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400 text-sm">
                No hay analisis aun. Ve a "Analizar Empresas" para comenzar.
              </div>
            ) : analyses.map(a => {
              const name = a.analysis?.company_name || a.scraped_data?.title || a.url;
              const score = a.analysis?.compatibility_score;
              const isSelected = selectedAnalysis?.id === a.id;
              return (
                <button
                  key={a.id}
                  onClick={() => setSelectedAnalysis(a)}
                  className={`w-full text-left p-3 rounded-lg border transition-all ${
                    isSelected ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-200' : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                  data-testid={`analysis-item-${a.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{name}</p>
                      <p className="text-xs text-slate-400 truncate mt-0.5">{a.url}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {score != null && <ScoreBadge score={score} />}
                      <StatusBadge status={a.status} />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Detail */}
          <div className="lg:col-span-3" data-testid="analysis-detail">
            {!selectedAnalysis ? (
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-400 text-sm">
                <Eye size={40} className="mx-auto mb-3 text-slate-300" />
                Selecciona un analisis para ver los detalles
              </div>
            ) : (
              <AnalysisDetail
                analysis={selectedAnalysis}
                onFindSimilar={handleFindSimilar}
                onCreateProspect={handleCreateProspect}
                onCreateProspectFromSimilar={handleCreateProspectFromSimilar}
                onDelete={handleDelete}
                similarLoading={similarLoading}
              />
            )}
          </div>
        </div>
      )}

      {/* ==================== COMPLIANCE TAB ==================== */}
      {tab === 'compliance' && (
        <div className="space-y-4" data-testid="compliance-section">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-lg font-bold text-slate-900 mb-1">Requisitos para Ser Subcontratista</h2>
            <p className="text-sm text-slate-500 mb-5">Documentacion y certificaciones que los General Contractors tipicamente solicitan. Prepara todo esto para acelerar el proceso de calificacion.</p>
            <div className="space-y-3">
              {checklist.map((cat, ci) => {
                const isOpen = expandedCats[cat.category] !== false; // open by default
                const requiredCount = cat.items.filter(i => i.required).length;
                return (
                  <div key={ci} className="border border-slate-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleCat(cat.category)}
                      className="w-full flex items-center gap-3 p-4 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                      data-testid={`compliance-cat-${ci}`}
                    >
                      {isOpen ? <CaretDown size={14} /> : <CaretRight size={14} />}
                      <span className="font-semibold text-sm text-slate-900 flex-1">{cat.category}</span>
                      <span className="text-xs text-slate-400">{requiredCount} obligatorios de {cat.items.length}</span>
                    </button>
                    {isOpen && (
                      <div className="divide-y divide-slate-100">
                        {cat.items.map((item, ii) => (
                          <div key={ii} className="p-4 flex gap-3" data-testid={`compliance-item-${ci}-${ii}`}>
                            <div className="pt-0.5 shrink-0">
                              {item.required
                                ? <Warning size={16} className="text-amber-500" weight="fill" />
                                : <CheckCircle size={16} className="text-slate-300" />}
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-slate-900">
                                {item.name}
                                {item.required && <span className="ml-2 text-xs text-red-500 font-normal">Obligatorio</span>}
                              </p>
                              <p className="text-xs text-slate-500 mt-1">{item.description}</p>
                              {item.alternative && (
                                <p className="text-xs text-blue-600 mt-1.5 bg-blue-50 px-2 py-1 rounded inline-block">
                                  Alternativa: {item.alternative}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AnalysisDetail({ analysis, onFindSimilar, onCreateProspect, onCreateProspectFromSimilar, onDelete, similarLoading }) {
  const a = analysis.analysis || {};
  const similar = analysis.similar_companies;
  const similarList = similar?.similar_companies || [];
  const hasSimilar = similarList.length > 0;
  const [showSimilar, setShowSimilar] = useState(hasSimilar);

  return (
    <div className="space-y-4">
      {/* Header card */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-slate-900" data-testid="company-name">{a.company_name || 'Sin nombre'}</h2>
            <p className="text-sm text-slate-500 mt-0.5 flex items-center gap-2">
              <Buildings size={14} /> {a.industry || 'N/A'}
              {a.headquarters && <><span className="text-slate-300">|</span> <MapPin size={14} /> {a.headquarters}</>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {a.compatibility_score != null && <ScoreBadge score={a.compatibility_score} />}
            <button onClick={() => onDelete(analysis.id)} className="p-1.5 text-slate-400 hover:text-red-500 transition-colors" data-testid="delete-analysis-btn">
              <Trash size={16} />
            </button>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap gap-2 mb-4">
          {analysis.status !== 'prospect_created' && (
            <button
              onClick={() => onCreateProspect(analysis.id)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:bg-emerald-700 transition-colors"
              data-testid="create-prospect-btn"
            >
              <UserPlus size={14} /> Crear Prospecto
            </button>
          )}
          {analysis.status === 'prospect_created' && (
            <span className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium border border-emerald-200">
              <CheckCircle size={14} weight="fill" /> Prospecto Creado
            </span>
          )}
          <button
            onClick={() => { setShowSimilar(true); onFindSimilar(analysis.id); }}
            disabled={similarLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white rounded-lg text-xs font-medium hover:bg-purple-700 disabled:opacity-50 transition-colors"
            data-testid="find-similar-btn"
          >
            {similarLoading ? <Spinner size={14} className="animate-spin" /> : <MagnifyingGlass size={14} />}
            {hasSimilar ? 'Actualizar Similares' : 'Buscar Similares'}
          </button>
          {hasSimilar && similarList.length > 0 && (
            <button
              onClick={async () => {
                if (!window.confirm(`Crear ${similarList.length} prospectos de empresas similares en el pipeline?`)) return;
                try {
                  const res = await axios.post(`${API_URL}/api/b2b/market-intel/create-prospects-from-similar-bulk/${analysis.id}`, {}, { withCredentials: true });
                  alert(`${res.data.created_count} prospectos creados. ${res.data.skipped_count} omitidos (ya existian).`);
                } catch (e) {
                  alert('Error: ' + (e.response?.data?.detail || e.message));
                }
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white rounded-lg text-xs font-medium hover:bg-amber-700 transition-colors"
              data-testid="bulk-create-similar-btn"
            >
              <UserPlus size={14} /> Crear TODOS los similares ({similarList.length})
            </button>
          )}
          <a
            href={analysis.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 text-slate-600 rounded-lg text-xs font-medium hover:bg-slate-50 transition-colors"
          >
            <Globe size={14} /> Ver Sitio Web
          </a>
        </div>

        {/* Key info grid */}
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <InfoBlock label="Tamano Estimado" value={a.company_size} />
          <InfoBlock label="Opera en Florida" value={a.operates_in_florida ? 'Si' : a.operates_in_florida === false ? 'No' : 'No determinado'} />
          <InfoBlock label="Necesita Low Voltage" value={a.needs_low_voltage ? 'Si' : a.needs_low_voltage === false ? 'No' : 'No determinado'} />
          <InfoBlock label="Tamano Oportunidad" value={a.opportunity_size} />
        </div>
      </div>

      {/* Services & Needs */}
      <div className="grid sm:grid-cols-2 gap-4">
        <ListCard title="Servicios que Ofrece" items={a.services_offered} />
        <ListCard title="Servicios LV que Necesita" items={a.low_voltage_services_needed} highlight />
      </div>

      {/* Compatibility & Strategy */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <div>
          <h4 className="text-sm font-semibold text-slate-700 mb-2">Razones de Compatibilidad</h4>
          <ul className="space-y-1">
            {(a.compatibility_reasons || []).map((r, i) => (
              <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                <CheckCircle size={14} className="text-emerald-500 mt-0.5 shrink-0" weight="fill" /> {r}
              </li>
            ))}
          </ul>
        </div>
        {a.approach_strategy && (
          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-1">Estrategia de Contacto</h4>
            <p className="text-sm text-slate-600 bg-blue-50 p-3 rounded-lg">{a.approach_strategy}</p>
          </div>
        )}
        {a.elevator_pitch && (
          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-1">Elevator Pitch</h4>
            <p className="text-sm text-slate-600 bg-emerald-50 p-3 rounded-lg italic">"{a.elevator_pitch}"</p>
          </div>
        )}
        {a.email_subject && (
          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-1">Asunto de Email Sugerido</h4>
            <p className="text-sm font-medium text-slate-800">{a.email_subject}</p>
          </div>
        )}
      </div>

      {/* Contacts found */}
      {a.key_contacts && a.key_contacts.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h4 className="text-sm font-semibold text-slate-700 mb-3">Contactos Identificados</h4>
          <div className="space-y-2">
            {a.key_contacts.map((c, i) => (
              <div key={i} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg text-sm">
                <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-xs">
                  {(c.name || '?')[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900">{c.name || 'N/A'}</p>
                  <p className="text-xs text-slate-400">{c.title || ''}</p>
                </div>
                {c.email && <span className="text-xs text-blue-600 truncate">{c.email}</span>}
                {c.phone && <span className="text-xs text-slate-500">{c.phone}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Scraped emails & phones */}
      {(analysis.scraped_data?.emails_found?.length > 0 || analysis.scraped_data?.phones_found?.length > 0) && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h4 className="text-sm font-semibold text-slate-700 mb-3">Datos de Contacto Extraidos del Sitio</h4>
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            {analysis.scraped_data.emails_found?.length > 0 && (
              <div>
                <p className="text-xs text-slate-400 mb-1">Emails</p>
                {analysis.scraped_data.emails_found.map((e, i) => (
                  <p key={i} className="text-blue-600">{e}</p>
                ))}
              </div>
            )}
            {analysis.scraped_data.phones_found?.length > 0 && (
              <div>
                <p className="text-xs text-slate-400 mb-1">Telefonos</p>
                {analysis.scraped_data.phones_found.map((p, i) => (
                  <p key={i} className="text-slate-700">{p}</p>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Risk factors */}
      {a.risk_factors && a.risk_factors.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h4 className="text-sm font-semibold text-slate-700 mb-2">Factores de Riesgo</h4>
          <ul className="space-y-1">
            {a.risk_factors.map((r, i) => (
              <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                <Warning size={14} className="text-amber-500 mt-0.5 shrink-0" weight="fill" /> {r}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Documents required */}
      {a.documents_likely_required && a.documents_likely_required.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h4 className="text-sm font-semibold text-slate-700 mb-2">Documentos Probablemente Requeridos</h4>
          <div className="flex flex-wrap gap-2">
            {a.documents_likely_required.map((d, i) => (
              <span key={i} className="px-2.5 py-1 bg-amber-50 text-amber-700 rounded-full text-xs border border-amber-200">{d}</span>
            ))}
          </div>
        </div>
      )}

      {/* Similar Companies */}
      {(showSimilar || hasSimilar) && (
        <div className="bg-white rounded-xl border border-purple-200 p-5" data-testid="similar-companies">
          <h4 className="text-sm font-semibold text-purple-700 mb-3 flex items-center gap-2">
            <Briefcase size={16} /> Empresas Similares Encontradas
          </h4>
          {similarLoading ? (
            <div className="flex items-center gap-3 p-4 text-purple-600">
              <Spinner size={20} className="animate-spin" />
              <span className="text-sm">Buscando empresas similares con IA...</span>
            </div>
          ) : similarList.length > 0 ? (
            <div className="space-y-2">
              {similarList.map((sc, i) => (
                <div key={i} className="border border-slate-200 rounded-lg p-3 hover:bg-slate-50 transition-colors" data-testid={`similar-company-${i}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900">{sc.company_name}</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {sc.industry} {sc.headquarters && `| ${sc.headquarters}`}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">{sc.why_similar}</p>
                      {sc.website && (
                        <a href={sc.website} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline mt-1 inline-block">{sc.website}</a>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {sc.estimated_compatibility && <ScoreBadge score={sc.estimated_compatibility} />}
                      <button
                        onClick={() => onCreateProspectFromSimilar(sc)}
                        className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                        title="Crear Prospecto"
                        data-testid={`create-similar-prospect-${i}`}
                      >
                        <UserPlus size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400">No se encontraron empresas similares.</p>
          )}

          {similar?.search_strategies && (
            <div className="mt-4 pt-3 border-t border-slate-100">
              <p className="text-xs font-medium text-slate-500 mb-1">Estrategias adicionales de busqueda:</p>
              <ul className="text-xs text-slate-400 space-y-0.5">
                {similar.search_strategies.map((s, i) => (
                  <li key={i} className="flex items-start gap-1.5"><ArrowRight size={10} className="mt-0.5 shrink-0" /> {s}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function InfoBlock({ label, value }) {
  return (
    <div className="bg-slate-50 rounded-lg p-2.5">
      <p className="text-xs text-slate-400">{label}</p>
      <p className="text-sm font-medium text-slate-800">{value || 'N/A'}</p>
    </div>
  );
}

function ListCard({ title, items, highlight }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <h4 className="text-sm font-semibold text-slate-700 mb-2">{title}</h4>
      <div className="flex flex-wrap gap-1.5">
        {items.map((item, i) => (
          <span key={i} className={`px-2 py-0.5 rounded-full text-xs border ${
            highlight ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-slate-50 text-slate-600 border-slate-200'
          }`}>{item}</span>
        ))}
      </div>
    </div>
  );
}

function DiscoveryResults({ discovery, onAnalyze, onCreateProspect, analyzingUrl }) {
  const companies = discovery?.result?.discovered_companies || [];
  const summary = discovery?.result?.search_summary || '';
  const tips = discovery?.result?.additional_search_tips || [];

  if (companies.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400 text-sm">
        No se encontraron empresas. Intenta con diferentes criterios.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="discovery-results">
      {/* Summary */}
      <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
        <p className="text-sm font-medium text-purple-900">
          <Compass size={16} className="inline mr-1.5" weight="fill" />
          {companies.length} empresas descubiertas
        </p>
        {summary && <p className="text-xs text-purple-600 mt-1">{summary}</p>}
      </div>

      {/* Companies grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {companies.map((c, i) => {
          const isAnalyzing = analyzingUrl === c.website;
          return (
            <div key={i} className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow" data-testid={`discovered-company-${i}`}>
              <div className="flex items-start justify-between mb-2">
                <h4 className="text-sm font-bold text-slate-900 leading-tight">{c.company_name}</h4>
                {c.estimated_compatibility != null && <ScoreBadge score={c.estimated_compatibility} />}
              </div>
              <p className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                <Buildings size={12} /> {c.industry}
                {c.headquarters && <span className="text-slate-300 mx-1">|</span>}
                {c.headquarters && <><MapPin size={12} /> {c.headquarters}</>}
              </p>
              {c.estimated_size && (
                <p className="text-xs text-slate-400 mb-1">Tamano: {c.estimated_size} {c.estimated_revenue && `| ${c.estimated_revenue}`}</p>
              )}
              <p className="text-xs text-slate-500 mt-2 line-clamp-2">{c.why_prospect}</p>

              {c.services && c.services.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {c.services.slice(0, 3).map((s, si) => (
                    <span key={si} className="px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded text-[10px]">{s}</span>
                  ))}
                  {c.services.length > 3 && <span className="text-[10px] text-slate-400">+{c.services.length - 3}</span>}
                </div>
              )}

              {c.confidence && (
                <p className={`text-[10px] mt-1.5 font-medium ${
                  c.confidence === 'High' ? 'text-emerald-600' : c.confidence === 'Medium' ? 'text-amber-600' : 'text-slate-400'
                }`}>
                  Confianza: {c.confidence}
                </p>
              )}

              <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
                {c.website && (
                  <button
                    onClick={() => onAnalyze(c)}
                    disabled={isAnalyzing}
                    className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
                    data-testid={`analyze-discovered-${i}`}
                  >
                    {isAnalyzing ? <Spinner size={12} className="animate-spin" /> : <Lightning size={12} weight="fill" />}
                    {isAnalyzing ? 'Analizando...' : 'Analizar'}
                  </button>
                )}
                <button
                  onClick={() => onCreateProspect(c)}
                  className="flex items-center justify-center gap-1.5 px-2 py-1.5 border border-emerald-200 text-emerald-700 rounded-lg text-xs font-medium hover:bg-emerald-50 transition-colors"
                  data-testid={`prospect-discovered-${i}`}
                >
                  <UserPlus size={12} /> Prospecto
                </button>
                {c.website && (
                  <a href={c.website} target="_blank" rel="noopener noreferrer"
                    className="flex items-center justify-center p-1.5 border border-slate-200 text-slate-500 rounded-lg text-xs hover:bg-slate-50 transition-colors">
                    <Globe size={12} />
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Search tips */}
      {tips.length > 0 && (
        <div className="bg-slate-50 rounded-xl border border-slate-200 p-4">
          <h4 className="text-xs font-semibold text-slate-500 mb-2">Tips adicionales de busqueda</h4>
          <ul className="space-y-1">
            {tips.map((t, i) => (
              <li key={i} className="text-xs text-slate-500 flex items-start gap-1.5">
                <ArrowRight size={10} className="mt-0.5 shrink-0 text-purple-500" /> {t}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}


function BidResults({ search, onCreateProspect }) {
  const opportunities = search?.result?.opportunities || [];
  const insights = search?.result?.market_insights || '';
  const regTips = search?.result?.registration_tips || [];
  const trends = search?.result?.upcoming_trends || [];
  const [statusFilter, setStatusFilter] = React.useState('all');

  if (opportunities.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400 text-sm">
        No se encontraron oportunidades. Intenta con diferentes criterios.
      </div>
    );
  }

  const typeColors = {
    'ITB': 'bg-blue-50 text-blue-700 border-blue-200',
    'RFP': 'bg-purple-50 text-purple-700 border-purple-200',
    'RFQ': 'bg-emerald-50 text-emerald-700 border-emerald-200',
    'Subcontract': 'bg-amber-50 text-amber-700 border-amber-200',
  };

  const catColors = {
    'Federal': 'text-blue-600',
    'Estado': 'text-purple-600',
    'Local': 'text-emerald-600',
    'Comercial': 'text-pink-600',
  };

  const getStatusInfo = (opp) => {
    const status = opp.status || 'Desconocido';
    if (status === 'Activa') return { label: 'Activa', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
    if (status === 'Expirada') return { label: 'Expirada', cls: 'bg-red-50 text-red-700 border-red-200' };
    return { label: 'Desconocido', cls: 'bg-slate-50 text-slate-600 border-slate-200' };
  };

  const filteredOpps = statusFilter === 'all'
    ? opportunities
    : opportunities.filter(opp => (opp.status || 'Desconocido') === statusFilter);

  const activasCount = opportunities.filter(o => o.status === 'Activa').length;
  const expiradasCount = opportunities.filter(o => o.status === 'Expirada').length;

  return (
    <div className="space-y-4" data-testid="bid-results">
      {/* Summary + Filter */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-sm font-medium text-amber-900">
            <Gavel size={16} className="inline mr-1.5" weight="fill" />
            {opportunities.length} oportunidades encontradas
          </p>
          <div className="flex items-center gap-1.5" data-testid="bid-status-filter">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${statusFilter === 'all' ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
            >
              Todas ({opportunities.length})
            </button>
            <button
              onClick={() => setStatusFilter('Activa')}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${statusFilter === 'Activa' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-emerald-600 border-emerald-200 hover:border-emerald-300'}`}
            >
              Activas ({activasCount})
            </button>
            <button
              onClick={() => setStatusFilter('Expirada')}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${statusFilter === 'Expirada' ? 'bg-red-600 text-white border-red-600' : 'bg-white text-red-600 border-red-200 hover:border-red-300'}`}
            >
              Expiradas ({expiradasCount})
            </button>
          </div>
        </div>
        {insights && <p className="text-xs text-amber-700 mt-1">{insights}</p>}
      </div>

      {/* Opportunities */}
      <div className="space-y-3">
        {filteredOpps.length === 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-6 text-center text-slate-400 text-sm">
            No hay oportunidades con el filtro seleccionado.
          </div>
        )}
        {filteredOpps.map((opp, i) => {
          const statusInfo = getStatusInfo(opp);
          return (
          <div key={i} className={`bg-white rounded-xl border p-4 hover:shadow-sm transition-shadow ${opp.status === 'Expirada' ? 'border-red-100 opacity-75' : 'border-slate-200'}`} data-testid={`bid-opportunity-${i}`}>
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-bold text-slate-900">{opp.title}</h4>
                <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                  <span className={`font-medium ${catColors[opp.category] || 'text-slate-600'}`}>{opp.category}</span>
                  <span className="text-slate-300">|</span>
                  <Buildings size={12} /> {opp.agency_or_company}
                  {opp.location && <><span className="text-slate-300">|</span><MapPin size={12} /> {opp.location}</>}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${statusInfo.cls}`} data-testid={`bid-status-${i}`}>
                  {statusInfo.label}
                </span>
                {opp.type && (
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${typeColors[opp.type] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                    {opp.type}
                  </span>
                )}
                {opp.compatibility_score != null && <ScoreBadge score={opp.compatibility_score} />}
              </div>
            </div>

            <p className="text-xs text-slate-600 mb-3">{opp.description}</p>

            <div className="grid sm:grid-cols-3 gap-2 mb-3">
              {opp.estimated_budget && (
                <div className="flex items-center gap-1.5 text-xs bg-slate-50 rounded-lg px-2.5 py-1.5">
                  <CurrencyDollar size={12} className="text-emerald-600" />
                  <span className="text-slate-600">{opp.estimated_budget}</span>
                </div>
              )}
              {opp.deadline_info && (
                <div className={`flex items-center gap-1.5 text-xs rounded-lg px-2.5 py-1.5 ${opp.status === 'Expirada' ? 'bg-red-50' : 'bg-slate-50'}`}>
                  <Clock size={12} className={opp.status === 'Expirada' ? 'text-red-500' : 'text-amber-500'} />
                  <span className={opp.status === 'Expirada' ? 'text-red-600' : 'text-slate-600'}>{opp.deadline_info}</span>
                </div>
              )}
              {opp.deadline_date && (
                <div className={`flex items-center gap-1.5 text-xs rounded-lg px-2.5 py-1.5 ${opp.status === 'Expirada' ? 'bg-red-50' : 'bg-emerald-50'}`}>
                  <Clock size={12} className={opp.status === 'Expirada' ? 'text-red-500' : 'text-emerald-500'} />
                  <span className={opp.status === 'Expirada' ? 'text-red-600 line-through' : 'text-emerald-700 font-medium'}>
                    {new Date(opp.deadline_date + 'T00:00:00').toLocaleDateString('es', { year: 'numeric', month: 'short', day: 'numeric' })}
                  </span>
                </div>
              )}
              {opp.source_platform && (
                <div className="flex items-center gap-1.5 text-xs bg-slate-50 rounded-lg px-2.5 py-1.5">
                  <Globe size={12} className="text-blue-500" />
                  <span className="text-slate-600">{opp.source_platform}</span>
                </div>
              )}
            </div>

            {opp.services_needed && opp.services_needed.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {opp.services_needed.map((s, si) => (
                  <span key={si} className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px] border border-blue-100">{s}</span>
                ))}
              </div>
            )}

            {opp.bid_strategy && (
              <p className="text-xs text-slate-500 bg-emerald-50 p-2 rounded-lg mb-2 border border-emerald-100">
                <Target size={12} className="inline mr-1 text-emerald-600" weight="fill" />
                {opp.bid_strategy}
              </p>
            )}

            {opp.requirements && opp.requirements.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {opp.requirements.map((r, ri) => (
                  <span key={ri} className="px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded text-[10px] border border-amber-100 flex items-center gap-0.5">
                    <Certificate size={8} /> {r}
                  </span>
                ))}
              </div>
            )}

            <div className="flex gap-2 pt-2 border-t border-slate-100 mt-2">
              <button
                onClick={() => onCreateProspect(opp)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:bg-emerald-700 transition-colors"
                data-testid={`bid-create-prospect-${i}`}
              >
                <UserPlus size={12} /> Crear Prospecto
              </button>
              {opp.source_url && (
                <a href={opp.source_url} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 text-slate-600 rounded-lg text-xs font-medium hover:bg-slate-50 transition-colors">
                  <ArrowSquareOut size={12} /> Ver en {opp.source_platform || 'Sitio'}
                </a>
              )}
              {opp.is_recurring && (
                <span className="flex items-center gap-1 px-2 py-1 text-[10px] text-blue-600 bg-blue-50 rounded-lg border border-blue-100">
                  <Clock size={10} /> Recurrente
                </span>
              )}
            </div>
          </div>
          );
        })}
      </div>

      {/* Trends & Tips */}
      {(trends.length > 0 || regTips.length > 0) && (
        <div className="grid sm:grid-cols-2 gap-4">
          {trends.length > 0 && (
            <div className="bg-slate-800 rounded-xl p-4 text-white">
              <h4 className="text-xs font-semibold mb-2 flex items-center gap-1.5"><Lightning size={12} weight="fill" className="text-amber-400" /> Tendencias 2026</h4>
              <ul className="space-y-1">
                {trends.map((t, i) => (
                  <li key={i} className="text-xs text-slate-300 flex items-start gap-1.5">
                    <ArrowRight size={10} className="mt-0.5 shrink-0 text-amber-400" /> {t}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {regTips.length > 0 && (
            <div className="bg-blue-50 rounded-xl border border-blue-200 p-4">
              <h4 className="text-xs font-semibold text-blue-800 mb-2 flex items-center gap-1.5"><CheckCircle size={12} weight="fill" /> Tips de Registro</h4>
              <ul className="space-y-1">
                {regTips.map((t, i) => (
                  <li key={i} className="text-xs text-blue-700 flex items-start gap-1.5">
                    <ArrowRight size={10} className="mt-0.5 shrink-0" /> {t}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function CompanyResults({ search, onCreateProspect }) {
  const companies = search?.result?.companies || [];
  const sectorInsights = search?.result?.sector_insights || '';
  const emailTips = search?.result?.email_tips || [];

  if (companies.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400 text-sm">
        No se encontraron empresas. Intenta con diferentes sectores o ubicaciones.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="company-results">
      {/* Summary */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
        <p className="text-sm font-medium text-indigo-900">
          <UsersThree size={16} className="inline mr-1.5" weight="fill" />
          {companies.length} empresas encontradas
        </p>
        {sectorInsights && <p className="text-xs text-indigo-700 mt-1">{sectorInsights}</p>}
      </div>

      {/* Companies */}
      <div className="grid sm:grid-cols-2 gap-3">
        {companies.map((company, i) => (
          <div key={i} className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-sm transition-shadow" data-testid={`company-result-${i}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-bold text-slate-900">{company.name}</h4>
                <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-indigo-600">{company.sector}</span>
                  {company.sub_sector && <><span className="text-slate-300">|</span><span>{company.sub_sector}</span></>}
                </p>
              </div>
              {company.potential_score != null && <ScoreBadge score={company.potential_score} />}
            </div>

            <div className="space-y-1.5 mb-3">
              {company.location && (
                <p className="text-xs text-slate-600 flex items-center gap-1.5">
                  <MapPin size={12} className="text-slate-400 shrink-0" /> {company.location}
                </p>
              )}
              {company.estimated_employees > 0 && (
                <p className="text-xs text-slate-600 flex items-center gap-1.5">
                  <UsersThree size={12} className="text-slate-400 shrink-0" /> ~{company.estimated_employees} empleados
                </p>
              )}
              {company.contact_email && (
                <p className="text-xs text-slate-600 flex items-center gap-1.5">
                  <EnvelopeSimple size={12} className="text-slate-400 shrink-0" /> {company.contact_email}
                </p>
              )}
              {company.phone && (
                <p className="text-xs text-slate-600 flex items-center gap-1.5">
                  <Phone size={12} className="text-slate-400 shrink-0" /> {company.phone}
                </p>
              )}
            </div>

            {company.why_needs_lv && (
              <p className="text-xs text-indigo-700 bg-indigo-50 p-2 rounded-lg mb-2 border border-indigo-100">
                <Lightning size={12} className="inline mr-1 text-indigo-500" weight="fill" />
                {company.why_needs_lv}
              </p>
            )}

            {company.potential_services && company.potential_services.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {company.potential_services.map((s, si) => (
                  <span key={si} className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px] border border-blue-100">{s}</span>
                ))}
              </div>
            )}

            {company.cold_email_angle && (
              <p className="text-xs text-slate-500 bg-amber-50 p-2 rounded-lg mb-2 border border-amber-100">
                <Target size={12} className="inline mr-1 text-amber-600" weight="fill" />
                {company.cold_email_angle}
              </p>
            )}

            <div className="flex gap-2 pt-2 border-t border-slate-100 mt-2">
              <button
                onClick={() => onCreateProspect(company)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:bg-emerald-700 transition-colors"
                data-testid={`company-create-prospect-${i}`}
              >
                <UserPlus size={12} /> Crear Prospecto
              </button>
              {company.website && (
                <a href={company.website.startsWith('http') ? company.website : `https://${company.website}`} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 text-slate-600 rounded-lg text-xs font-medium hover:bg-slate-50 transition-colors">
                  <Globe size={12} /> Sitio Web
                </a>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Email Tips */}
      {emailTips.length > 0 && (
        <div className="bg-indigo-50 rounded-xl border border-indigo-200 p-4">
          <h4 className="text-xs font-semibold text-indigo-800 mb-2 flex items-center gap-1.5">
            <EnvelopeSimple size={12} weight="fill" /> Tips para Cold Emailing
          </h4>
          <ul className="space-y-1">
            {emailTips.map((t, i) => (
              <li key={i} className="text-xs text-indigo-700 flex items-start gap-1.5">
                <ArrowRight size={10} className="mt-0.5 shrink-0" /> {t}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
